import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Spinner,
  SimpleGrid,
  IconButton,
  Code,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { ArrowBackIcon, RepeatIcon } from "@chakra-ui/icons";

// ─── Job-urile expuse manual (cheile trebuie să existe în RunJob.php) ──
const JOBS = [
  {
    key: "daily_insight",
    title: "Daily AI Insight",
    icon: "🤖",
    color: "purple",
    cron: "0 9 * * * (daily, 09:00)",
    description:
      "Generates today's portfolio insight with Gemini and saves it to daily_insights. Also creates the snapshot behind the Portfolio Growth chart.",
    warning: "Writes a new row and calls the Gemini API.",
  },
  {
    key: "weekly_coach",
    title: "Weekly AI Coach",
    icon: "🎯",
    color: "teal",
    cron: "0 10 * * 1 (Mondays, 10:00)",
    description:
      "Generates the weekly coaching message with Gemini and saves it to weekly_coach.",
    warning: "Writes a new row and calls the Gemini API.",
  },
  {
    key: "next_buy",
    title: "Next Buy Advisor",
    icon: "🛒",
    color: "purple",
    cron: "manual only — run before a purchase round",
    description:
      "Scores every dividend holding (income, dividend record, sector concentration, position size, 52-week valuation, payment calendar), then has Gemini write the report on what to buy next. Saves to next_buy and updates the 🛒 Next Buy card on the home page.",
    warning: "Writes a new row, calls Gemini and Yahoo Finance. Not investment advice.",
    manual: true, // nu e în cron — vechimea nu trebuie semnalată ca întârziere
  },
  {
    key: "crypto_prices",
    title: "Crypto Price Refresh",
    icon: "₿",
    color: "orange",
    description:
      "Fetches current prices for every symbol in crypto_price, updates them and appends a row to history_price_crypto.",
    warning: "Updates prices and appends price history.",
  },
];

const fmtAge = (hours) => {
  if (hours === null || hours === undefined) return "never";
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const ageColor = (hours) => {
  if (hours === null || hours === undefined) return "gray";
  if (hours < 26) return "green";
  if (hours < 24 * 8) return "yellow";
  return "red";
};

const JobCard = ({ job, status, onRun, isRunning, result }) => (
  <Box
    bg="gray.800"
    borderWidth="1px"
    borderColor="whiteAlpha.200"
    borderRadius="xl"
    p={5}
    boxShadow="lg"
    transition="all 0.2s"
    _hover={{ borderColor: `${job.color}.600` }}
  >
    <HStack justify="space-between" align="flex-start" mb={3}>
      <HStack spacing={3} align="flex-start">
        <Text fontSize="2xl" lineHeight={1}>
          {job.icon}
        </Text>
        <VStack align="start" spacing={0}>
          <Heading size="sm" color={`${job.color}.300`}>
            {job.title}
          </Heading>
          {job.cron && (
            <Text fontSize="2xs" color="gray.500" fontFamily="mono">
              {job.cron}
            </Text>
          )}
        </VStack>
      </HStack>

      <Badge
        colorScheme={job.manual ? "gray" : ageColor(status?.age_hours)}
        fontSize="2xs"
        px={2}
        py={1}
        borderRadius="md"
      >
        {fmtAge(status?.age_hours)}
      </Badge>
    </HStack>

    <Text fontSize="xs" color="gray.400" mb={2}>
      {job.description}
    </Text>

    <Text fontSize="2xs" color="orange.300" mb={3}>
      ⚠ {job.warning}
    </Text>

    <HStack justify="space-between" mb={result ? 3 : 0}>
      <VStack align="start" spacing={0}>
        <Text fontSize="2xs" color="gray.600">
          {status?.detail || "—"}
        </Text>
        {status?.last_run && (
          <Text fontSize="2xs" color="gray.600" fontFamily="mono">
            {status.last_run}
          </Text>
        )}
      </VStack>

      <Button
        size="sm"
        colorScheme={job.color}
        onClick={() => onRun(job)}
        isLoading={isRunning}
        loadingText="Running"
        leftIcon={<RepeatIcon />}
      >
        Run now
      </Button>
    </HStack>

    {result && (
      <Box>
        <Divider borderColor="whiteAlpha.200" mb={2} />
        <HStack spacing={2} mb={2} flexWrap="wrap">
          <Badge colorScheme={result.success ? "green" : "red"} fontSize="2xs">
            {result.success ? "SUCCESS" : result.timed_out ? "TIMED OUT" : "FAILED"}
          </Badge>
          <Text fontSize="2xs" color="gray.500">
            exit {result.exit_code} · {(result.duration_ms / 1000).toFixed(1)}s · {result.finished_at}
          </Text>
        </HStack>
        <Code
          display="block"
          whiteSpace="pre-wrap"
          fontSize="2xs"
          bg="gray.900"
          color="gray.300"
          p={2}
          borderRadius="md"
          maxH="180px"
          overflowY="auto"
        >
          {result.output}
        </Code>
      </Box>
    )}
  </Box>
);

const AdminSettings = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [statuses, setStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const [results, setResults] = useState({});

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${proxy}server_mts/admin/GetJobStatus.php`, {
        withCredentials: true,
        headers: { "Cache-Control": "no-cache" },
      });
      setStatuses(res.data?.jobs || {});
    } catch {
      toast({
        title: "Could not load job status",
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const runJob = async (job) => {
    if (!window.confirm(`Run "${job.title}" now?\n\n${job.warning}`)) return;

    setRunningJob(job.key);
    try {
      const res = await axios.post(
        `${proxy}server_mts/admin/RunJob.php`,
        { job: job.key },
        { withCredentials: true }
      );
      const data = res.data;
      setResults((prev) => ({ ...prev, [job.key]: data }));

      toast({
        title: data.success ? `${job.title} finished` : `${job.title} failed`,
        description: data.success
          ? `Completed in ${(data.duration_ms / 1000).toFixed(1)}s`
          : data.timed_out
          ? "The job timed out"
          : `Exit code ${data.exit_code}`,
        status: data.success ? "success" : "error",
        duration: 4000,
        position: "top",
      });

      fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setResults((prev) => ({
        ...prev,
        [job.key]: {
          success: false,
          exit_code: "n/a",
          duration_ms: 0,
          finished_at: new Date().toISOString().slice(0, 19).replace("T", " "),
          output: msg,
        },
      }));
      toast({
        title: "Request failed",
        description: msg,
        status: "error",
        duration: 4000,
        position: "top",
      });
    } finally {
      setRunningJob(null);
    }
  };

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={3}>
              <IconButton
                aria-label="Back"
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
              />
              <VStack align="start" spacing={0}>
                <Heading size="md" bgGradient="linear(to-r, teal.300, blue.400)" bgClip="text">
                  ⚙️ Settings
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Run scheduled jobs manually
                </Text>
              </VStack>
            </HStack>

            <IconButton
              aria-label="Refresh status"
              icon={<RepeatIcon />}
              variant="ghost"
              colorScheme="teal"
              onClick={fetchStatus}
              isDisabled={isLoading}
            />
          </HStack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxW="1400px" py={6}>
        <VStack align="stretch" spacing={5}>
          <Box
            bg="whiteAlpha.50"
            borderLeft="3px solid"
            borderColor="teal.400"
            borderRadius="md"
            px={4}
            py={3}
          >
            <Text fontSize="xs" color="gray.400">
              These are the same scripts cron runs on the server. Running one here does exactly what
              the scheduled run does — including writing to the database and calling external APIs.
              The badge shows how long ago each job last produced a result.
            </Text>
          </Box>

          {isLoading ? (
            <HStack justify="center" py={10}>
              <Spinner color="teal.300" />
              <Text color="gray.400" fontSize="sm">
                Loading job status…
              </Text>
            </HStack>
          ) : (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
              {JOBS.map((job) => (
                <JobCard
                  key={job.key}
                  job={job}
                  status={statuses[job.key]}
                  result={results[job.key]}
                  isRunning={runningJob === job.key}
                  onRun={runJob}
                />
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default AdminSettings;
